import _ from 'lodash'
import awesomeDebouncePromise from 'awesome-debounce-promise'
import * as ViewActions from './Actions'
import * as SendActions from './send'
import * as PayRequestActions from './pay-request'
import { fetch, post } from '../../calculator/lib/xhr'
import {
  ActionTypes as types,
  ERROR_COMPLIANCE_DENIED,
  PAYMENT_TYPE_FLOW_MAP,
  TRANSFER_BASE_URL,
  ERROR_PAYMENT_FAILURE,
} from '../constants'
import { routeTo } from '../utils/routeHelper'
import { prepareXBInitialState } from '../utils/xbFeeCalcUtils'
import {
  trackGuestClickPmtChoice,
  trackImpressionUnifiedSend,
} from '../utils/unified-send-analytics'
import { changeUnilateralCountryCode } from './unilateral'
import { removeMailto } from '../utils/remove-mailto'
import { tracking } from '../../lib/analytics/mParticle'
import Events from '../../lib/analytics/eventTags'
import { Preview } from 'transfer/lazy-components'
import { hasContactConfidenceTreatment } from 'experiments/contact-confidence'
import { isBizSearchTreatment } from 'experiments/bizSearch'
import { isV2PricingReq } from 'experiments/v2'
import {
  getIsConfidentInRecipient,
  getHasSentToRecipient,
} from '../../lib/recipient'
import {
  getRecipientFromContact,
  getHasSentToContact,
} from '../../lib/contact-utils'
import { isPhoneNumberMaybe } from 'transfer/utils/phoneUtils'
import { preloadImage } from '../../lib/image-utils'
import { defaultUnilateralRecipientToSenderCountry } from 'lib/unilateral-utils'
import {
  toggleContactConfidenceOverpanel,
  toggleUnilateralRecipientChoiceOverpanel,
} from './overpanels'
import { getContactConfidenceAnalyticsData } from 'transfer/utils/contact-confidence-analytics'
import {
  PAYMENT_TYPE,
  MONEY_PAGE,
  PRODUCT_FLOWS,
  ENV_TYPE,
} from '../../../../lib/constants'
import { getFlowType, parseSearch } from '../utils/url-utils'
import { trackSearchResultImpression } from '../utils/directory-search-analytics'
import reduxStore from '../stores/configureStore'
import { trackActivity } from '../../lib/analytics/fpti'
import logger from 'loglevel'
import { isIframe } from 'iframe-support'
import { handleInterstitialCipFlow } from '../utils/homeInfoUtils'
import { getTrafficSource } from '../utils/mgmUtils'
import { showError } from './Actions'
import { getSessionDataPayload } from 'transfer/utils/session'

/**
 * Fetch contact emails/phones so the user can select which one to send/request to/from. It may not be clear
 * which piece of information should be used if the contact has multiple pieces of contact information. If
 * there is only one contact method (i.e. a single email/phone) then skip the overpanel and continue the flow
 * {FormattedContact} contact - contact that was selected by user to send/request to/from
 * {Function} onSingleRecipient - callback function in the case that there is only a single recipient
 */
export function fetchUnilateralRecipientChoices({
  contact = {},
  onSingleRecipient = () => {},
} = {}) {
  return (dispatch, getState) => {
    dispatch({ type: types.RECIPIENT.REQUEST_UNILATERAL_RECIPIENT_CHOICES })

    return fetch(`/myaccount/transfer/peers/${contact.id}`)
      .then(({ emails = [], phones = [] } = {}) => {
        dispatch({
          type: types.RECIPIENT.RECEIVED_UNILATERAL_RECIPIENT_CHOICES,
        })

        if (
          (emails.length === 1 && phones.length === 0) ||
          (emails.length === 0 && phones.length === 1)
        ) {
          const updatedContact = { ...contact }
          if (emails.length) {
            Object.assign(updatedContact, {
              email: emails[0],
              phones: [],
            })
          } else {
            Object.assign(updatedContact, {
              email: '',
              phones: [phones[0]],
            })
          }

          onSingleRecipient(updatedContact)
        } else {
          dispatch(
            ViewActions.selectUnilateralContact({ ...contact, emails, phones })
          )

          dispatch(toggleUnilateralRecipientChoiceOverpanel({ show: true }))
        }
      })
      .catch(ViewActions.handleError(dispatch))
  }
}

export function fetchRecipientDetails({
  contact,
  linkName,
  isContact,
  enableAnalytics,
  flowType,
  enableContactConfidence = false,
}) {
  // preload preview chunk
  Preview.preload()

  contact = removeMailto(contact) // remove 'mailto:' from email
  const recipient = getRecipientFromContact(contact)

  return (dispatch, getState) => {
    dispatch({ type: types.CROSS_BORDER.REQUEST_RECIPIENT_DETAILS }) // Show spinner
    return fetch('/myaccount/transfer/recipientDetails', {
      recipient,
      lastTransactionType: contact.transaction_type,
      flowType,
      payerId: contact.payerId,
      networkId: contact.networkId,
    })
      .then(
        ({
          data: { recipientDetails = {}, beaconClientMetaDataId = '' },
        } = {}) => {
          if (beaconClientMetaDataId) {
            dispatch({
              type: types.TRANSACTION.SET_FRAUDNET_CLIENT_METADATA,
              data: { beaconClientMetaDataId },
            })
          }

          if (recipientDetails.triggerSenderPhoneConfirmation) {
            dispatch({ type: types.USER.TRIGGER_SENDER_PHONE_CONFIRMATION })
          }
          const analytics = { linkName, enableAnalytics, isContact }

          checkSenderElgibilty(recipientDetails, dispatch)

          if (
            // We only block the sender at this point for 2 cases
            // 1.If both personal and purchase payments are disabled
            (recipientDetails.isSenderPersonalPaymentDisabled &&
              recipientDetails.isSenderPurchasePaymentDisabled) ||
            // 2.If the sender clicks on the recipient bubble
            ((recipientDetails.isSenderPersonalPaymentDisabled ||
              recipientDetails.isSenderPurchasePaymentDisabled) &&
              contact.transaction_type)
          ) {
            return checkandRedirectToHomeinfo(
              recipientDetails,
              analytics,
              contact
            )
          }

          handleRecipientDetails({
            recipientDetails,
            contact,
            analytics,
            store: getState(),
            dispatch,
            enableContactConfidence,
          })

          trackServiceAndClientDecision(recipientDetails)
        }
      )
      .catch(ViewActions.handleError(dispatch))
  }
}

/*
e:"ac"
[FPTI]
page:"main:p2p:tab-contacts:decision"
pgrp:"main:p2p:tab-contacts:decision"
[Amplitude]
event_name:"client_server_display_decision_consensus_reached"

default_payment_type:"{"client":<CLIENT_VALUE>,"service":<SERVICE_VALUE>}"
ui_variant:"{"client":<CLIENT_VALUE>,"service":<SERVICE_VALUE>}"
payment_type_selection_behavior:"{"client":<CLIENT_VALUE>,"service":<SERVICE_VALUE>}"
cal_correlation_id:<CAL_CORRELATION_ID>
product_entry_point:""
usage_mode:""
suggested_payment_type:"{"client":<CLIENT_VALUE>,"service":<SERVICE_VALUE>}"
service_client_match: "y" if client/services is matching else "n"
 */
export function trackServiceAndClientDecision(recipientDetails) {
  const { serviceContext = {}, suggestedPaymentType } = recipientDetails
  const pageGroup = 'main:p2p:tab-contacts:decision'
  const pageName = 'main:p2p:tab-contacts:decision'

  const default_payment_type = `{"client":${serviceContext.defaultPaymentType},"service":${serviceContext.defaultPaymentType}}`
  const ui_variant = `{"client":${serviceContext.moneyPageOverPanel},"service":${serviceContext.moneyPageOverPanel}}`
  const payment_type_selection_behavior = `{"client":${serviceContext.selectionBehavior},"service":${serviceContext.selectionBehavior}}`
  const suggested_payment_type = `{"client":${suggestedPaymentType},"service":${serviceContext.suggestedPaymentType}}`

  let service_client_match = 'n'
  if (suggestedPaymentType === serviceContext.suggestedPaymentType) {
    service_client_match = 'y'
  }

  const extraData = {
    e: 'ac',
    default_payment_type,
    ui_variant,
    payment_type_selection_behavior,
    cal_correlation_id: serviceContext.correlationId,
    product_entry_point: serviceContext.productEntryPoint,
    usage_mode: serviceContext.usageMode,
    suggested_payment_type,
    service_client_match,
  }

  if (
    serviceContext.usageMode === 'SHADOW' ||
    serviceContext.usageMode === 'LIVE'
  ) {
    trackActivity(pageGroup, pageName, extraData)
    tracking({
      event_name: 'client_server_display_decision_consensus_reached',
      ...extraData,
    })
  }
}

export function updateTxn() {
  return function(dispatch, getState) {
    const {
      transaction: { preselectedFundingOption },
      amount: { amount, currencyCode },
      recipient: {
        recipient,
        payerId,
        displayName,
        token,
        networkId,
        thumbnailUrl,
      },
      crossBorder: { fxQuoteId },
      note: { note },
      server: { triggerSenderPhoneConfirmation },
    } = getState()

    const payload = {
      recipient,
      amount,
      currencyCode,
      payerId,
      networkId,
      note,
      token,
      fxQuoteId,
    }
    dispatch({ type: types.CROSS_BORDER.REQUEST_RECIPIENT_DETAILS }) // Show spinner

    post('/myaccount/transfer/fi/update-sender-receiver', payload)
      .then(response => {
        updateTxnSuccess(response, dispatch, {
          isV2: true,
          triggerSenderPhoneConfirmation,
          phoneConfirmData: {
            flowType: getFlowType(),
            amount,
            currencyCode,
            payerId,
            recipient,
            displayName,
            thumbnailUrl,
          },
          preselectedFundingOption,
        })
      })
      .catch(err => {
        logger.error(err)
        updateTxnFail(dispatch, preselectedFundingOption)
      })
  }
}

export function sendMoreInfoToAssessCapabilities() {
  return function(dispatch, getState) {
    const {
      transaction: { preselectedFundingOption },
      amount: { amount, currencyCode },
      recipient: { recipient, payerId, displayName, networkId, thumbnailUrl },
      note: { note },
      server: { triggerSenderPhoneConfirmation },
    } = getState()

    const payload = {
      recipient,
      amount,
      currencyCode,
      payerId,
      networkId,
      note,
    }
    dispatch({ type: types.CROSS_BORDER.REQUEST_RECIPIENT_DETAILS }) // Show spinner
    post('/myaccount/transfer/fi/reassess-capabilities', payload)
      .then(response => {
        updateTxnSuccess(response, dispatch, {
          triggerSenderPhoneConfirmation,
          phoneConfirmData: {
            amount,
            currencyCode,
            payerId,
            recipient,
            displayName,
            thumbnailUrl,
          },
          preselectedFundingOption,
        })
      })
      .catch(ViewActions.handleError(dispatch))
  }
}

function updateTxnSuccess(response, dispatch, options = {}) {
  const {
    data: { serviceContext, suggestedPaymentType },
  } = response

  if (!options.isV2) {
    trackServiceAndClientDecision({ serviceContext, suggestedPaymentType })
  }

  if (response.data && response.data.paymentTypeSelectionMessage) {
    dispatch({
      type: types.TRANSACTION.UPDATE_PAYMENT_TYPE_SELECTION_MESSAGE,
      data: {
        message: response.data.paymentTypeSelectionMessage,
      },
    })
  }

  if (response.data && response.data.paymentTypeTogglePersonalMessage) {
    dispatch({
      type: types.TRANSACTION.UPDATE_PAYMENT_TYPE_TOGGLE_PERSONAL_MESSAGE,
      data: {
        message: response.data.paymentTypeTogglePersonalMessage,
      },
    })
  }

  if (response.data && response.data.paymentTypeTogglePurchaseMessage) {
    dispatch({
      type: types.TRANSACTION.UPDATE_PAYMENT_TYPE_TOGGLE_PURCHASE_MESSAGE,
      data: {
        message: response.data.paymentTypeTogglePurchaseMessage,
      },
    })
  }

  if (
    response.data &&
    response.data.paymentTypeTransitions &&
    !_.isEmpty(response.data.paymentTypeTransitions)
  ) {
    dispatch({
      type: types.TRANSACTION.UPDATE_PAYMENT_TYPE_TRANSITIONS,
      data: {
        paymentTypeTransitions: response.data.paymentTypeTransitions,
      },
    })
  }

  if (response.data && response.data.token) {
    dispatch({
      type: types.TRANSACTION.UPDATE_TOKEN,
      data: {
        token: response.data.token,
        moreInfoIsNeededForAssessCapabilities:
          response.data.paymentTypeSelectionDecision !== 'COMPLETED',
      },
    })
  }

  dispatch({
    type: types.TRANSACTION.UPDATE_SUGGESTED_DESIGN,
    data: { suggestedDesign: response.data.moneyPageOverPanel },
  })

  dispatch({
    type: types.TRANSACTION.UPDATE_MONEY_PAGE,
    data: { moneyPageOverPanel: response.data.moneyPageOverPanel },
  })

  dispatch({
    type: types.TRANSACTION.CHANGE_HIGHLIGHTED_PAYMENT_TYPE,
    data: {
      highlightPaymentType: response.data.highlightPaymentType,
    },
  })

  dispatch({
    type: types.TRANSACTION.CHANGE_IS_HARD_DEFAULT,
    data: { isHardDefault: response.data.isHardDefault },
  })

  if (response.data.receiverFee) {
    dispatch({
      type: types.AMOUNT.ADD_RECEIVER_FEE,
      data: { fee: response.data.receiverFee },
    })
  }

  dispatch({ type: types.OVERPANELS.STOP_LOADING }) // Hide spinner

  // if reassess call returned suggestedPaymentType, we should default to buy/send instead of showing overpanel
  if (
    [PAYMENT_TYPE.PERSONAL, PAYMENT_TYPE.PURCHASE].includes(
      response.data.suggestedPaymentType
    )
  ) {
    if (options.triggerSenderPhoneConfirmation) {
      options.phoneConfirmData.flowType =
        PAYMENT_TYPE_FLOW_MAP[response.data.suggestedPaymentType]
      return dispatch(redirectToDolphin(options.phoneConfirmData))
    }

    dispatch(
      SendActions.fetchFundingOptions({
        preselectedFundingOption: options.preselectedFundingOption,
      })
    )
    return routeTo(
      `/homepage/${
        PAYMENT_TYPE_FLOW_MAP[response.data.suggestedPaymentType]
      }/preview`
    )
  }

  if (
    options.isV2 &&
    options.phoneConfirmData &&
    (options.phoneConfirmData.flowType === 'buy' ||
      options.phoneConfirmData.flowType === 'send')
  ) {
    if (options.triggerSenderPhoneConfirmation) {
      return dispatch(redirectToDolphin(options.phoneConfirmData))
    }

    return dispatch(
      SendActions.fetchFundingOptions({
        preselectedFundingOption: options.preselectedFundingOption,
      })
    )
  }

  return dispatch(togglePaymentTypeChoiceOverpanel({ show: true }))
}

function updateTxnFail(dispatch, preselectedFundingOption) {
  dispatch({ type: types.OVERPANELS.STOP_LOADING }) // Hide spinner

  dispatch({
    type: types.TRANSACTION.UPDATE_MONEY_PAGE,
    data: { moneyPageOverPanel: MONEY_PAGE.CONTROL_WEB },
  })

  const flowType = getFlowType()
  if (flowType === 'buy' || flowType === 'send') {
    return dispatch(
      SendActions.fetchFundingOptions({
        preselectedFundingOption: preselectedFundingOption,
      })
    )
  }

  dispatch(togglePaymentTypeChoiceOverpanel({ show: true }))
}

function checkSenderElgibilty(recipientDetails, dispatch) {
  if (recipientDetails.isSenderPersonalPaymentDisabled) {
    dispatch({
      type: types.USER.DISABLE_PERSONAL_PAYMENT,
      data: {
        isSenderPersonalPaymentDisabled:
          recipientDetails.isSenderPersonalPaymentDisabled,
        personalCipUrl: recipientDetails.personalCipUrl,
      },
    })
  }

  if (recipientDetails.isSenderPurchasePaymentDisabled) {
    dispatch({
      type: types.USER.DISABLE_PURCHASE_PAYMENT,
      data: {
        isSenderPurchasePaymentDisabled:
          recipientDetails.isSenderPurchasePaymentDisabled,
        purchaseCipUrl: recipientDetails.purchaseCipUrl,
      },
    })
  }

  if (recipientDetails.requireSenderInstrumentAddition) {
    dispatch({
      type: types.USER.REQUIRE_INSTRUMENT_ADDITION,
      data: {
        requireSenderInstrumentAddition:
          recipientDetails.requireSenderInstrumentAddition,
      },
    })
  }
}

function checkandRedirectToHomeinfo(recipientDetails, analytics, contact) {
  const { payerId } = contact
  const {
    suggestedPaymentType,
    isSenderPersonalPaymentDisabled,
    isSenderPurchasePaymentDisabled,
  } = recipientDetails
  const { isContact } = analytics
  const params = {
    flowType: suggestedPaymentType === 'PURCHASE' ? 'buy' : 'send',
    payerId: payerId,
  }
  if (isSenderPersonalPaymentDisabled && isSenderPurchasePaymentDisabled) {
    return handleInterstitialCipFlow()
  }
  if (
    (isSenderPersonalPaymentDisabled || isSenderPurchasePaymentDisabled) &&
    isContact
  ) {
    return handleInterstitialCipFlow(params)
  }
}

/**
 * Redirect to Dolphin Flow for Phone Confirmation
 * @param data
 * @returns {Function}
 */
export function redirectToDolphin(data) {
  return (dispatch, getState) => {
    const {
      server: { env = '', isMgm = false, flowIntent },
      unilateral: { name, countryCode },
      recipient: { isUnilateral, token },
    } = getState()
    const {
      flowType,
      amount,
      currencyCode,
      payerId,
      recipient,
      displayName,
      thumbnailUrl,
    } = data

    const mgmTrafficSource = getTrafficSource()

    const phoneConfirmationPayload = {
      token,
      flowType,
      amount,
      currencyCode,
      payerId,
      recipient,
      displayName,
      thumbnailUrl,
      env,
      isMgm,
      mgmTrafficSource,
      ...(isUnilateral && {
        unilateralName: name,
        unilateralCountryCode: countryCode,
      }),
      flowIntent,
      ...getSessionDataPayload(),
    }

    const phoneConfirmationFlowUrl =
      '/myaccount/transfer/flow/fi/phoneconfirmation'
    const mainWindow = isIframe() ? window.top : window
    post(phoneConfirmationFlowUrl, phoneConfirmationPayload)
      .then(response => {
        const { redirectURL = {} } = response
        mainWindow.location.assign(redirectURL)
      })
      .catch(err => {
        logger.error(err)
      })
  }
}

export function redirectToHomeinfo(data) {
  return (dispatch, getState) => {
    dispatch({
      type: types.EXTERNAL.REDIRECT_TO_HOMEINFO,
    })
    handleInterstitialCipFlow(data)
  }
}

export function proceedWithClaim() {
  return dispatch => {
    dispatch({
      type: types.UNILATERAL.CLAIM_PROCESS_LINKING,
    })

    const currentUrl = window.location.pathname || ''
    const pathArray = currentUrl.split('/')
    const filteredPathArray = pathArray.filter(p => p !== '')
    const idx = filteredPathArray.length - 1
    const txnNum = filteredPathArray.length > 0 ? filteredPathArray[idx] : ''
    const { skipV2, contextData } = parseSearch()

    if (!txnNum && !contextData) {
      dispatch({
        type: types.UNILATERAL.CLAIM_ERROR,
        data: { isLinkingFailed: true },
      })
    }

    const linkingType =
      filteredPathArray[idx - 1] === 'reveal'
        ? 'reveal/revealLinking'
        : 'claimLinking'

    let url = `/myaccount/transfer/${linkingType}?contextData=${contextData}`

    // TODO: needs to be removed after first launch of claim money changes in URL, this is to support fallback
    if (!contextData && txnNum) {
      url = `/myaccount/transfer/${linkingType}/${txnNum}`
    }

    post(url, { skipV2: !!+skipV2 })
      .then(() => {
        dispatch({
          type: types.UNILATERAL.CLAIM_SUCCESS,
          data: { isLinkingSuccess: true },
        })
      })
      .catch(err => {
        if (err.code === ERROR_COMPLIANCE_DENIED) {
          dispatch(
            showError(err, {
              onClosed: function() {
                routeTo(TRANSFER_BASE_URL, { refresh: true, replace: true })
              },
            })
          )
        }

        dispatch({
          type: types.UNILATERAL.CLAIM_ERROR,
          data: {
            ...(ERROR_PAYMENT_FAILURE === err.code && {
              settingsError: err.message,
              settingsErrorCode: err.code,
            }),
            isLinkingFailed: true,
          },
        })
      })
  }
}

export function togglePaymentTypeChoiceOverpanel({
  show,
  isGuest = false,
  analytics = {},
}) {
  return (dispatch, getState) => {
    const { recipient, amount, note } = getState()
    if (show) {
      if (isGuest) {
        trackGuestClickPmtChoice()
      }
      tracking(
        Events.p2p_payment_type_screen_shown({
          suggested_protection_design: recipient.suggestedDesign,
          receiver_country: recipient.country,
          receiver_currency: amount.currencyCode,
          sender_currency: amount.fromCurrencyCode,
          protection_design: recipient.moneyPageOverPanel,
          receiver_account_type: recipient.recipientAccountType,
          note_character_length: note.note.length,
          payment_type_selection_message: recipient.paymentTypeSelectionMessage
            ? 'y'
            : 'n',
        })
      )
      trackImpressionUnifiedSend('paymentChoice', analytics)
      // remove flfr so that it does not confuse preview page
      window.flfr = null
    }
    dispatch({
      type: types.OVERPANELS.TOGGLE_PAYMENT_TYPE_CHOICE_OVERPANEL,
      data: { show },
    })
  }
}

export function fetchCountryData(toCountryCode) {
  return function(dispatch) {
    if (toCountryCode) {
      dispatch({
        type: types.CROSS_BORDER.REQUEST_COUNTRY_DATA,
        data: { toCountryCode },
      })

      const url = `/myaccount/transfer/fx/countrydata/${toCountryCode}`

      return fetch(url, {})
        .then(response => {
          dispatch(receiveCountryData(toCountryCode, response))
        })
        .catch(ViewActions.handleErrorFromFailedRequest(dispatch))
    }
    return Promise.resolve()
  }
}

function receiveCountryData(data, json) {
  return {
    type: types.CROSS_BORDER.RECEIVED_COUNTRY_DATA,
    data: json.data,
    receivedAt: Date.now(),
  }
}

// updates transaction data on the preview page after changing flows
export function updateTransactionData({ isPurchase }) {
  return function(dispatch, getState) {
    dispatch(ViewActions.changeIsPurchase(isPurchase))
    const {
      transaction: { hasFetchedFundingOptions, preselectedFundingOption },
      crossBorder: {
        showXBCalculator,
        fromCurrencyCode,
        fromCountryCode,
        toCountryCode,
        toCurrencyCode,
        destinationAmount: amount,
      },
      recipient: { token },
      server: { env },
    } = getState()
    // if we've already fetched funding options, re-fetch to update fees
    if (hasFetchedFundingOptions) {
      if (getFlowType() === 'payRequest') {
        dispatch(PayRequestActions.fetchFundingOptions())
      } else {
        dispatch(SendActions.fetchFundingOptions({ preselectedFundingOption }))
      }

      // if we haven't fetched yet, we're on the first half with the 'continue' button
      // in this case, we need to fetch the exchange rate (for xb only)
      // because F&F and G&S rates can be different
    } else if (showXBCalculator && !isV2PricingReq(env)) {
      dispatch(
        ViewActions.fetchOriginAmount({
          fromCurrencyCode,
          fromCountryCode,
          toCountryCode,
          toCurrencyCode,
          isPurchase,
          amount,
          // not actually true but we need to force a loader for the exchange rate
          currencyChanged: true,
          token,
        })
      )
    }
  }
}

function updateRecipientCurrencies({
  recipientCurrencyCode,
  recipientCurrencies,
  recipientCurrencyMap,
}) {
  return {
    type: types.AMOUNT.UPDATE_RECIPIENT_CURRENCIES,
    data: { recipientCurrencyCode, recipientCurrencies, recipientCurrencyMap },
  }
}

export function setDefaultPurchaseProtection(value) {
  return {
    type: types.INSTRUMENTATION.PURCHASE_PROTECTION,
    data: { defaultPurchaseProtection: value },
  }
}

export function clearPurchaseProtection() {
  return {
    type: types.INSTRUMENTATION.CLEAR_PURCHASE_PROTECTION,
    data: {},
  }
}

export function changedPurchaseProtection(data) {
  return {
    type: types.INSTRUMENTATION.PURCHASE_PROTECTION,
    data,
  }
}

/**
 * handle recipient details returned from server
 @ @param {Object} options
 * @param {RecipientDetail} options.recipientDetails - result returned from server
 * @param {FormattedContact} options.contact - the contact that is selected
 * @param {Object} options.analytics - analytics stuff
 * @param {Object} options.store - redux store state
 * @param {Function} options.dispatch - redux dispatch
 * @param {boolean} options.enableContactConfidence - whether to enable recipient confidence or not
 */
export function handleRecipientDetails({
  recipientDetails = {},
  contact = {},
  analytics = {},
  store = {},
  dispatch = () => {},
  enableContactConfidence = false,
}) {
  dispatch({ type: types.CROSS_BORDER.RECEIVED_RECIPIENT_DETAILS }) // Hide spinner
  dispatch({
    type: types.TRANSACTION.CHANGE_HIGHLIGHTED_PAYMENT_TYPE,
    data: { highlightPaymentType: recipientDetails.highlightPaymentType },
  })

  if (recipientDetails.paymentTypeSelectionMessage) {
    dispatch({
      type: types.TRANSACTION.UPDATE_PAYMENT_TYPE_SELECTION_MESSAGE,
      data: {
        message: recipientDetails.paymentTypeSelectionMessage,
      },
    })
  }

  if (recipientDetails.paymentTypeTogglePersonalMessage) {
    dispatch({
      type: types.TRANSACTION.UPDATE_PAYMENT_TYPE_TOGGLE_PERSONAL_MESSAGE,
      data: {
        message: recipientDetails.paymentTypeTogglePersonalMessage,
      },
    })
  }

  if (recipientDetails.paymentTypeTogglePurchaseMessage) {
    dispatch({
      type: types.TRANSACTION.UPDATE_PAYMENT_TYPE_TOGGLE_PURCHASE_MESSAGE,
      data: {
        message: recipientDetails.paymentTypeTogglePurchaseMessage,
      },
    })
  }

  if (recipientDetails.isRecipientCurrencyRestricted) {
    dispatch({
      type: types.TRANSACTION.RECIPIENT_CURRENCY_RESTRICTED,
      data: {
        isRecipientCurrencyRestricted:
          recipientDetails.isRecipientCurrencyRestricted,
      },
    })
  }

  if (recipientDetails.isNoteRestricted) {
    dispatch({
      type: types.TRANSACTION.NOTE_FIELD_IS_RESTRICTED,
      data: {
        isNoteRestricted: recipientDetails.isNoteRestricted,
      },
    })
  }

  dispatch({
    type: types.TRANSACTION.CHANGE_RECOMMENDED_PAYMENT_TYPE,
    data: { recommendedPaymentType: recipientDetails.recommendedPaymentType },
  })

  if (
    recipientDetails.paymentTypeTransitions &&
    !_.isEmpty(recipientDetails.paymentTypeTransitions)
  ) {
    dispatch({
      type: types.TRANSACTION.UPDATE_PAYMENT_TYPE_TRANSITIONS,
      data: {
        paymentTypeTransitions: recipientDetails.paymentTypeTransitions,
      },
    })
  }

  dispatch({
    type: types.TRANSACTION.CHANGE_IS_HARD_DEFAULT,
    data: { isHardDefault: recipientDetails.isHardDefault },
  })

  const { linkName, enableAnalytics, isContact } = analytics
  const {
    recipient,
    suggestedPaymentType,
    paymentTypes,
    recipientCountryIsInferred,
    isUnilateral,
    thumbnailUrl,
    hasNetworkId,
    isRestricted,
    recurringType,
    recipientAccountType,
    mayCashAdvanceFeesApply,
  } = recipientDetails
  const {
    user: { country: senderCountryCode = '' } = {},
    contacts: {
      contacts = [],
      searchedContacts = [],
      hasFetchedContacts = false,
    } = {},
  } = store
  const isGSOnlyRecipient = paymentTypes && !paymentTypes.includes('PERSONAL')
  const isPhone = isPhoneNumberMaybe(recipient)

  if (thumbnailUrl) {
    // force client preload recipient profile image
    preloadImage(thumbnailUrl)
  }

  // keep track of whether recipient's country supports F&F
  // so we can hide the button to change payment types
  dispatch(
    ViewActions.selectContact(
      { ...contact, isGSOnlyRecipient },
      linkName,
      isContact,
      recipientDetails,
      enableAnalytics
    )
  )

  // set sender and recipient currency list
  const data = prepareXBInitialState(recipientDetails, store)
  dispatch(ViewActions.setXBInitialState(data))
  dispatch(updateRecipientCurrencies(recipientDetails))

  if (defaultUnilateralRecipientToSenderCountry(senderCountryCode)) {
    dispatch(changeUnilateralCountryCode(senderCountryCode))
  } else if (recipientCountryIsInferred) {
    // autofill the country code for the user based on what we inferred.
    // they can change it if we got it wrong
    // this will also have the effect of always sending the country code
    // even if we're not in the RTR case (and therefore don't require
    // the user provide the country).
    dispatch(changeUnilateralCountryCode(recipientDetails.recipientCountryCode))
  }

  const hasSentToRecipient = isContact
    ? getHasSentToContact(contact)
    : getHasSentToRecipient(
        recipient,
        [...contacts, ...searchedContacts],
        hasFetchedContacts
      )

  const isConfidentInRecipient =
    !hasContactConfidenceTreatment() ||
    Boolean(
      !enableContactConfidence ||
        getIsConfidentInRecipient(isUnilateral, hasSentToRecipient)
    )

  return handleTransactionDetails(
    isConfidentInRecipient,
    getSkipPaymentType(suggestedPaymentType),
    suggestedPaymentType,
    dispatch,
    {
      isUnilateral,
      hasNetworkId,
      isRestricted,
      isPhone,
      isContact,
      recurringType,
      suggestedPaymentType,
      recipientAccountType,
      mayCashAdvanceFeesApply,
    }
  )
}

/**
 * Handle transaction details from server and surface appropriate overpanels
 * @param {boolean} isConfidentInRecipient - whether sender needs confidence in recipient or not
 * @param {boolean} skipPaymentType - whether to skip payment type choice overpanel or not
 * @param {PayType} suggestedPaymentType - suggested payment type for transaction
 * @param {Function} dispatch - redux dispatch function
 * @param {Object} analytics - object for analytic purpose
 */
export function handleTransactionDetails(
  isConfidentInRecipient,
  skipPaymentType,
  suggestedPaymentType,
  dispatch,
  analytics = {}
) {
  if (isConfidentInRecipient && skipPaymentType) {
    return routeTo(
      `/homepage/${PAYMENT_TYPE_FLOW_MAP[suggestedPaymentType]}/preview`
    )
  }

  dispatch(ViewActions.setSuggestedTransactionType(suggestedPaymentType))

  if (!isConfidentInRecipient) {
    dispatch(
      toggleContactConfidenceOverpanel({
        show: true,
        analytics: getContactConfidenceAnalyticsData(analytics),
      })
    )
  } else {
    return routeTo(`/homepage/preview`)
  }
}

/**
 * Determine whether or not to skip payment type selection during send flow
 * @param {PaymentType} suggestedPaymentType - suggested payment type for transaction
 * @returns {boolean} whether or not to skip payment type selection
 */

export function getSkipPaymentType(suggestedPaymentType) {
  return (
    suggestedPaymentType === PAYMENT_TYPE.PERSONAL ||
    suggestedPaymentType === PAYMENT_TYPE.PURCHASE
  )
}

/**
 * Called when first entering a flow. Fetches peers from the server via AJAX
 * call and calls {@link receivedContacts} with the response data.
 */
export function fetchPeers() {
  return (dispatch, getState) => {
    const {
      contacts: { hasFetchedContacts, hasUpdatedContacts } = {},
      server: { env = '' },
    } = getState()
    // The pisp flow does not require fetching contacts
    // Fetching contacts can potentially cause user to be autologged out
    // if proper scopes are not in place due to being unauthorized
    if (env === ENV_TYPE.PISP) {
      return
    }

    if (!hasFetchedContacts || hasUpdatedContacts) {
      fetch('/myaccount/transfer/peers')
        .then(response => {
          dispatch(ViewActions.receivedContacts(response))
        })
        .catch(() => {})
    }
  }
}

export function receivedBlockedPeers({ blockedContacts = [] }) {
  return {
    type: types.CONTACTS.RECEIVED_BLOCKED_CONTACTS,
    data: {
      blockedContacts,
    },
  }
}

export function fetchBlockedPeers() {
  return (dispatch, getState) => {
    dispatch({ type: types.CONTACTS.LOADING_BLOCKED_CONTACTS })

    return fetch('/myaccount/transfer/peers/blocked').then(response => {
      dispatch(receivedBlockedPeers(response))
    })
  }
}

/**
 * Make post request for performing search or load more
 * @param {Object} data - data (body) of the request
 */
const retrievePeers = data => {
  return post('/myaccount/transfer/peers/search', data)
}

// Get the searchDebounceTime from redux store, use the default value if failed.
const getSearchDebounceTime = () => {
  let time = 500
  const { searchDebounceTime } = reduxStore.getState().server
  if (searchDebounceTime && typeof searchDebounceTime === 'number') {
    time = searchDebounceTime
  }
  return time
}

// use awesomeDebouncePromise to ensure we only handle the response
// returned from the last request.
export const retrievePeersDebounced = awesomeDebouncePromise(
  retrievePeers,
  getSearchDebounceTime()
)

export function searchPeers({
  query,
  includeDirect = true,
  includeNonDirect = false,
  isRequestFlow = false,
  isMgm = false,
} = {}) {
  return dispatch => {
    dispatch({ type: types.PEERS.SEARCHING_PEERS })
    const data = {
      query,
      identifier: isRequestFlow ? PRODUCT_FLOWS.REQUEST : PRODUCT_FLOWS.SEND,
      direct: {
        include: includeDirect,
      },
      nonDirect: {
        include: includeNonDirect,
      },
      bizDirect: {
        include: isBizSearchTreatment() ? includeDirect : false,
      },
      bizNonDirect: {
        include: isBizSearchTreatment() ? includeNonDirect : false,
      },
    }
    const response = retrievePeersDebounced(data)
    response
      .then(res => {
        const yourContacts = res.directContacts ? res.directContacts : []
        const peopleOnPaypal = res.nonDirectContacts
          ? res.nonDirectContacts
          : []
        dispatch(receivedSearchedPeers(res, isMgm))
        dispatch({ type: types.PEERS.RETRIEVED_PEERS })
        trackSearchResultImpression(
          isRequestFlow,
          yourContacts.length,
          peopleOnPaypal.length
        )
      })
      .catch(() => {
        dispatch({ type: types.PEERS.SEARCH_ERROR })
      })
  }
}

/**
 * Handle returned result of directory search
 * @param {Array} directContacts - search results of direct contacts
 * @param {Array} nonDirectContacts - search results of nondirect contacts
 * @param {number} directTotalPages - Total pages of direct contacts (for pagination)
 * @param {number} nonDirectTotalPages - Total pages of nondirect contacts (for pagination)
 * @returns {Object} action data for contact reducer
 */
export function receivedSearchedPeers(
  {
    directContacts = [],
    nonDirectContacts = [],
    nonDirectTotalPages = 0,
    bizDirectContacts = [],
    bizNonDirectContacts = [],
    bizNonDirectTotalPages = 0,
  },
  isMgm
) {
  // Filter the unilateral contacts for MGM flow (Member Gets Member)
  directContacts = isMgm
    ? directContacts.filter(data => data.isUnilateral)
    : directContacts
  nonDirectContacts = isMgm
    ? nonDirectContacts.filter(data => data.isUnilateral)
    : nonDirectContacts
  bizDirectContacts = isMgm
    ? bizDirectContacts.filter(data => data.isUnilateral)
    : bizDirectContacts
  bizNonDirectContacts = isMgm
    ? bizNonDirectContacts.filter(data => data.isUnilateral)
    : bizNonDirectContacts

  return {
    type: types.PEERS.RECEIVED_SEARCHED_PEERS,
    data: {
      directContacts,
      nonDirectContacts,
      nonDirectTotalPages,
      bizDirectContacts,
      bizNonDirectContacts,
      bizNonDirectTotalPages,
      showSearchError: false,
    },
    receivedAt: Date.now(),
  }
}

/**
 * Handle returned result of load more results (pagination)
 * @param {Array} nonDirectContacts - search results of nondirect contacts
 * @returns {Object} action data for contact reducer
 */
export function receivedLoadedPeers({ nonDirectContacts = [] }) {
  return {
    type: types.PEERS.RECEIVED_LOADED_PEERS,
    data: {
      nonDirectContacts,
      showSearchError: false,
    },
    receivedAt: Date.now(),
  }
}

/**
 * Handle returned result of load more results (pagination)
 * @param {Array} nonDirectContacts - search results of nondirect contacts
 * @returns {Object} action data for contact reducer
 */
export function receivedLoadedBusinesses({ bizNonDirectContacts = [] }) {
  return {
    type: types.PEERS.RECEIVED_LOADED_BUSINESSES,
    data: {
      bizNonDirectContacts,
      showSearchError: false,
    },
    receivedAt: Date.now(),
  }
}

export function receivedContactActivities({ transactions }, contactId) {
  return {
    type: types.CONTACTS.RECEIVED_CONTACT_DATA,
    data: {
      [contactId]: transactions,
    },
    receivedAt: Date.now(),
  }
}

export function fetchContactActivities(contactId) {
  const url = `/myaccount/transfer/peers/${contactId}/activity`
  return (dispatch, getState) => {
    if (getState().contacts.activityData[contactId] || !contactId) {
      return
    }
    fetch(url)
      .then(response => {
        dispatch(receivedContactActivities(response, contactId))
      })
      .catch(ViewActions.handleError(dispatch))
  }
}

/**
 * Initialize MGM Flow
 */
export function initializeMGMFlow(data) {
  return {
    type: types.TRANSACTION.MGM_INIT,
    data,
  }
}

/**
 * Initialize MGM Flow
 */
export function togglePhoneConfirmation(data) {
  return {
    type: types.EXTERNAL.TOGGLE_PHONE_CONFIRMATION,
    data,
  }
}

/**
 * Initializes MGM Amount to 0.01
 */

export function initializeMGMAmount() {
  return {
    type: types.TRANSACTION.MGM_CUSTOM_FIELDS,
  }
}

/**
 * Show MGM Recipient Error
 */

export function showMgmRecipientError() {
  return {
    type: types.RECIPIENT.MGM_RECIPIENT_ERROR,
  }
}

export function toggleEncryptionFailed({ show = false }) {
  return {
    type: types.ERROR.ENCRYPTION_FAILED,
    data: { show },
  }
}

export function funnelStarted({ funnel }) {
  return {
    type: types.TRANSACTION.FUNNEL_STARTED,
    data: { funnel },
  }
}

/**
   - fire contact engaged event 1x per attempt ID
   - fire on the first of either
       a) start typing on search input
       b) click on top contact bubble
       c) amount screen if dropping into the amount screen directly
 */
export function contactEngaged() {
  return {
    type: types.TRANSACTION.CONTACT_ENGAGED,
  }
}

export function fetchFundingOptionsAndShowFundingOptionsOverpanel() {
  const flowType = getFlowType()
  const fetchFundingOptions =
    flowType === 'payRequest'
      ? PayRequestActions.fetchFundingOptions
      : SendActions.fetchFundingOptions

  return (dispatch, getState) => {
    const { preselectedFundingOption } = getState().transaction
    dispatch(
      fetchFundingOptions({
        ...(preselectedFundingOption && { preselectedFundingOption }),
      })
    )
  }
}
